import { useState, type KeyboardEvent } from 'react';
import type { AddressPrediction, GeoBounds, GeoCoordinates } from '@repo/domain';
import { getBoundsFromCircle, useAddressPredictionActions, useAddressPredictionStream } from '@repo/domain-service';
import { useGeoPosition } from '@repo/geo-position';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { useDebouncedCallback } from 'use-debounce';

import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';
import { MAP_ZOOM_AREA_LEVEL } from '@shared/constants/map';

const testId = TestId.root('addressSearch');
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;
const BLUR_TIMEOUT_MS = 200;
const DEFAULT_COUNTRY_CODE = 'pl';
const GEOLOCATION_BIAS_RADIUS_METERS = 15000;

interface Params {
  value?: string;
  onChange?: (value: string, coordinates?: GeoCoordinates) => void;
}

interface Return {
  predictions: AddressPrediction[];
  showPredictions: boolean;
  activeIndex: number;
  handleInputChange: (inputValue: string) => void;
  handlePredictionSelect: (placeId: string) => void;
  handleInputBlur: () => void;
  handleInputFocus: () => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export function useAddressSearch({ value, onChange }: Params): Return {
  const { position, repanFunction } = useGeoPosition();
  const [showPredictions, setShowPredictions] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const setSelectedParking = useParkingSearchStore((state) => state.setSelectedParking);
  const mapBounds = useParkingSearchStore((state) => state.mapBounds);
  const searchedAddressLocation = useParkingSearchStore((state) => state.searchedAddress.location);

  const { addressPredictions: predictions, search, clear } = useAddressPredictionStream();
  const { getAddressPredictions } = useAddressPredictionActions();

  const getBiasBounds = (): GeoBounds | undefined => {
    if (mapBounds) return mapBounds;
    if (position) return getBoundsFromCircle(position, GEOLOCATION_BIAS_RADIUS_METERS);
    return undefined;
  };

  const debouncedSearch = useDebouncedCallback((query: string) => {
    search({ query, countryCode: DEFAULT_COUNTRY_CODE, bounds: getBiasBounds() });
    setShowPredictions(true);
  }, DEBOUNCE_DELAY);

  const handleInputChange = (inputValue: string) => {
    setActiveIndex(-1);
    if (onChange) {
      onChange(inputValue);
    }

    if (inputValue.length >= MIN_SEARCH_LENGTH) {
      debouncedSearch(inputValue);
    } else {
      debouncedSearch.cancel();
      void clear();
      setShowPredictions(false);
    }
  };

  const handlePredictionSelect = async (placeId: string) => {
    const prediction = predictions.find((candidate) => candidate.id === placeId);
    if (prediction && onChange) {
      onChange(prediction.description);
    }
    debouncedSearch.cancel();
    setShowPredictions(false);

    const detailedResults = await getAddressPredictions({ id: placeId }, { withDetails: true });
    const detailed = detailedResults[0];

    if (!detailed?.coordinates) return;

    if (prediction && onChange) {
      onChange(prediction.description, detailed.coordinates);
    }

    setSelectedParking(undefined, undefined);

    if (repanFunction) {
      repanFunction(detailed.coordinates, MAP_ZOOM_AREA_LEVEL);
    }

    void clear();
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowPredictions(false);
    }, BLUR_TIMEOUT_MS);
  };

  const handleInputFocus = () => {
    if (value && value.length >= MIN_SEARCH_LENGTH && predictions.length > 0) {
      setShowPredictions(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && predictions.length === 0) {
      event.preventDefault();
      // After rehydrate, predictions are empty but the previously-selected
      // address may still be paired with valid coordinates — use them instead
      // of forcing the user to retype.
      if (searchedAddressLocation) {
        setSelectedParking(undefined, undefined);
        if (repanFunction) {
          repanFunction(searchedAddressLocation, MAP_ZOOM_AREA_LEVEL);
        }
        return;
      }
      toast.danger(tr('parking_search:address.no_results'), testId.append('noAddressResults'));
      return;
    }

    if (!showPredictions || predictions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = activeIndex >= 0 ? activeIndex : 0;
      handlePredictionSelect(predictions[index].id);
      setActiveIndex(-1);
    }
  };

  return {
    predictions,
    showPredictions,
    activeIndex,
    handleInputChange,
    handlePredictionSelect,
    handleInputBlur,
    handleInputFocus,
    handleKeyDown,
  };
}
